import {
  ContentSwitcher,
  Scrollbar,
  Text,
  type PlaybackTransport,
} from "@carbon6/c6-frontend-design";
import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { hatcheryTextModes } from "@/app/theme";
import { RefreshIcon } from "@/components/icons";
import { EmptyState } from "@/shared/ui/emptyState/public";
import { LoadingState } from "@/shared/ui/loadingState/public";
import type { EpisodeDiagnosticDetail } from "../diagnosticsSeries";
import { diagnosticSeriesStatusText } from "../diagnosticsStatusText";
import {
  channelTitle,
  type ChannelStructureIndex,
  type ParquetChannelGroupId,
} from "../parquetChannels";
import type { PlayerViewport } from "@/components/video";
import { motionViewportWindow } from "./motionSeries";
import { motionSeriesSource } from "./motionRendering";
import { usePlaybackCssVar } from "../segments/usePlaybackCssVar";
import { useMotionDetail, useMotionOverview } from "./useMotionSeries";
import {
  isExtraMotionMetric,
  isPrimaryMotionMetric,
  motionCardsWithMetrics,
  motionChannelCards,
} from "./motionChannelCards";
import { MotionChannelGrid, hiddenSeriesKey } from "./MotionChannelGrid";
import { GripperSegmentRows } from "./GripperSegmentRows";
import styles from "./MotionSeriesView.module.css";

type Props = {
  detail: EpisodeDiagnosticDetail | null;
  onRefresh?: () => void;
  detailError?: string;
  enabled: boolean;
  // 리뷰에서는 같은 데이터와 설정을 유지하고 그리퍼만 표시한다.
  gripperOnly?: boolean;
  channels: ChannelStructureIndex;
  transport: PlaybackTransport | null;
  // 아래 공용 재생 바가 그리는 시간 창. 이 보기는 이 창과 개요 범위의 교집합을 쓴다. 창이
  // 없으면(재생 바 없음·영상 없음) 에피소드 전체 범위를 쓴다.
  viewport?: PlayerViewport | null;
  onSeekToSeconds?: (seconds: number) => void;
};

/**
 * 로딩 상태는 **개요가 도착할 때까지만** 표시한다.
 *
 * 개요 JSON 은 모든 채널의 곡선을 이미 가진다(창원 실측 1.4 MB). 상세는 그 10배(14 MB)다.
 * 상세를 모두 받을 때까지 빈 화면을 보여 주면 첫 그림까지의 시간이 열 배가 된다. 그래서 개요로
 * 먼저 그리고, 상세가 도착하면 바꾼다. 개요 표본으로 그린 선은 옅게 그려 아직 정밀하지 않음을
 * 표시한다.
 */
const LOADING_TEXT = "운동 데이터를 불러오고 있습니다.";
const DETAIL_LOADING_TEXT = "정밀 표본을 불러오고 있습니다. 지금 곡선은 개요 표본입니다.";

// 지표 탭. 탭은 카드 격자에 담을 지표만 고른다. 채널과 계열은 카드가 구분한다.
const METRIC_TABS = [
  {
    value: "motion",
    label: "운동·그리퍼",
    tooltip: "채널마다 속력·가속도 크기를 보고, 그리퍼는 원본 값과 구간을 함께 봅니다.",
  },
  {
    value: "extra",
    label: "추가 지표",
    tooltip: "속력 증감률과 성분별 토크처럼 상류가 함께 낸 나머지 지표를 봅니다.",
  },
] as const;

/**
 * 운동 지표 보기. **채널마다 카드 한 장**을 두고 그 안에 시간별 지표를 그린다.
 *
 * 이름이 같은 Observation·Action 은 한 차트에 겹쳐 그린다. 명령대로 움직였는지는 두 선을 나란히
 * 놓아야 알 수 있다. 차트를 오가며 보면 y축 눈금도 바뀌어 비교할 수 없다.
 *
 * Owns:
 * - 재생 바가 준 창과 개요 범위의 교집합을 모든 카드에 같은 창으로 전달한다.
 * - 재생선 CSS 변수(스크롤 판 한 곳에서만 구독한다).
 * - 개요 → 상세 교체와 그 상태 문구, 꺼진 계열 집합.
 *
 * Does not own:
 * - **확대 창.** 아래 공용 재생 바(c6 `MediaTimelineBar`)의 `[-] 100% [+]` 가 정한다. 화면의 줌
 *   UI 는 하나다. 재생 위치가 창을 벗어나면 그 재생 바가 창을 옮긴다. 이 보기가 창을 옮기는
 *   방법은 seek 하나뿐이다.
 * - 카드 목록·정렬·계열 표기(`motionChannelCards.ts`), 표본·y축(`motionRendering.ts`).
 */
export function MotionSeriesView({
  detail,
  onRefresh,
  detailError,
  enabled,
  gripperOnly = false,
  channels,
  transport,
  viewport = null,
  onSeekToSeconds,
}: Props): ReactNode {
  const [retry, setRetry] = useState(0);
  const refresh = () => {
    onRefresh?.();
    setRetry((value) => value + 1);
  };
  const [tab, setTab] = useState<"motion" | "extra">("motion");
  // 꺼진 계열만 담는다. 기본값이 "켬"이므로 새 카드는 초기화 없이 켜진 상태로 표시된다. 이
  // 값은 탭을 바꿔도 유지된다. 에피소드를 바꾸면 이 보기 전체가 리셋된다. 상위가 `key` 를
  // 에피소드로 주기 때문이다.
  const [hidden, setHidden] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = useCallback(
    (cardId: string, group: ParquetChannelGroupId, shown: boolean) =>
      setHidden((current) => {
        const next = new Set(current);
        if (shown) next.delete(hiddenSeriesKey(cardId, group));
        else next.add(hiddenSeriesKey(cardId, group));
        return next;
      }),
    [],
  );
  const [scroll, setScroll] = useState<HTMLDivElement | null>(null);
  const overviewState = useMotionOverview(
    detail?.motionOverviewSeries?.url ?? null,
    enabled && detail !== null,
    retry,
  );
  const overview = overviewState.status === "success" ? overviewState.data : null;
  const full = { start: overview?.start_seconds ?? 0, end: overview?.end_seconds ?? 0 };
  const window = viewport && overview ? motionViewportWindow(viewport, overview) : full;
  const samples = useMotionDetail(
    detail?.motionDetailSeries?.url ?? null,
    overview,
    enabled,
    retry,
  );
  // 카드와 그리퍼가 같은 색인을 쓴다. 먼저 개요로 그리고, (채널, 지표)마다 상세로 바꾼다.
  // 렌더마다 색인을 다시 만들면 `lines` 식별자가 바뀐다. 그러면 `MotionPlot` 의 표시 표본
  // 메모가 무효가 되어 모든 차트를 다시 그린다.
  const source = useMemo(
    () =>
      motionSeriesSource(overview?.series ?? [], samples.status === "success" ? samples.data : []),
    [overview, samples],
  );
  // 카드 목록은 **개요가 정한다**. 상세가 도착할 때 목록이 바뀌면 카드가 리마운트된다. 그러면
  // 체크박스와 y축 토글 상태가 사라진다. 개요와 상세는 같은 (채널, 지표) 집합을 가진다.
  const cards = useMemo(
    () => motionChannelCards(overview?.series ?? [], channels),
    [overview, channels],
  );
  // 차트를 누르거나 끌면 영상 위치만 옮긴다. 창 밖 지점을 보여주는 일도 seek 로 처리한다.
  // 재생선이 창을 벗어나면 재생 바가 창을 그 지점으로 옮기고, 새 창을 이 보기에 전달한다.
  const select = (seconds: number) => {
    const time = Math.max(full.start, Math.min(full.end, seconds));
    onSeekToSeconds?.(Math.max(0, time));
  };
  // 재생선은 **스크롤 판 한 곳**에서만 구독한다. 모든 차트가 창을 공유하므로 위치도 하나다.
  // CSS 변수는 상속된다. 차트마다 구독하면 같은 값을 계산하는 리스너가 채널 수만큼 늘어난다.
  usePlaybackCssVar(scroll, enabled ? transport : null, "--motion-playhead", (snapshot) => {
    const t = snapshot.currentTimeSeconds;
    return t < window.start || t > window.end || window.end <= window.start
      ? null
      : (t - window.start) / (window.end - window.start);
  });
  // 창이 밖에서(재생 바 줌·팬) 바뀌면, 영상이 멈춰 있어도 재생선 좌표를 바로 갱신한다.
  // 구독은 재생 이벤트로만 실행된다. 그래서 창이 움직이는 동안은 이 effect 만 좌표를 고친다.
  useEffect(() => {
    if (!scroll || !transport || !enabled) return;
    const time = transport.getSnapshot().currentTimeSeconds;
    const visible = time >= window.start && time <= window.end && window.end > window.start;
    scroll.dataset.playhead = visible ? "on" : "off";
    if (visible)
      scroll.style.setProperty(
        "--motion-playhead",
        String((time - window.start) / (window.end - window.start)),
      );
  }, [scroll, transport, enabled, window.start, window.end]);
  const refreshAction = { label: "새로고침", onClick: refresh, icon: <RefreshIcon /> };
  const retryAction = { label: "다시 시도", onClick: refresh, icon: <RefreshIcon /> };
  if (detailError)
    return (
      <EmptyState tone="error" action={retryAction}>
        {detailError}
      </EmptyState>
    );
  if (!detail) return <LoadingState>{LOADING_TEXT}</LoadingState>;
  if (detail.motionOverviewSeries?.status !== "ready")
    return (
      <EmptyState action={refreshAction}>
        {diagnosticSeriesStatusText(detail.motionOverviewSeries, "시간별 운동")}
      </EmptyState>
    );
  if (overviewState.status === "loading" || overviewState.status === "idle")
    return <LoadingState>{LOADING_TEXT}</LoadingState>;
  if (overviewState.status === "error")
    return (
      <EmptyState tone="error" action={retryAction}>
        {overviewState.message}
      </EmptyState>
    );
  if (!overview)
    return (
      <EmptyState action={refreshAction}>
        운동 개요 파일을 찾을 수 없습니다. 다시 확인해 주세요.
      </EmptyState>
    );
  // 상세는 **화면을 막지 않는다**. 개요 곡선이 이미 표시되어 있다. 그래서 상태·오류는 격자 위
  // 한 줄로 알린다. 정밀 표본이 오면 알림 없이 곡선을 바꾼다.
  const detailNotice =
    detail.motionDetailSeries?.status !== "ready"
      ? {
          tone: "info" as const,
          text: diagnosticSeriesStatusText(detail.motionDetailSeries, "운동 상세"),
        }
      : detail.motionDetailSeries.url === null
        ? { tone: "info" as const, text: "운동 상세 파일의 조회 주소가 없습니다." }
        : samples.status === "error"
          ? { tone: "error" as const, text: samples.message }
          : samples.status !== "success"
            ? { tone: "busy" as const, text: DETAIL_LOADING_TEXT }
            : null;
  const grid = {
    source,
    window,
    transport: enabled ? transport : null,
    onSelect: select,
    hidden,
    onToggle: toggle,
  };
  return (
    <div className={styles.root} data-gripper-only={gripperOnly || undefined}>
      <Scrollbar
        className={styles.detailScroll}
        gutter="auto"
        aria-label={gripperOnly ? "그리퍼 그래프 스크롤" : "운동 상세 스크롤"}
        ref={setScroll}
      >
        {!gripperOnly ? (
          <div className={styles.toolbar}>
            <ContentSwitcher
              label="운동 지표 보기"
              size="sm"
              options={METRIC_TABS}
              value={tab}
              onValueChange={(value) => {
                if (value === "motion" || value === "extra") setTab(value);
              }}
            />
            <Text variant={hatcheryTextModes["caption-normal"]} className={styles.toolbarHint}>
              픽셀마다 첫·끝·최소·최대 표본을 보존합니다. 짧은 peak 가 평균으로 지워지지 않습니다.
            </Text>
            <span className={styles.spacer} />
            {/* 이 보기에는 확대 컨트롤이 없다. 그래서 창 범위를 어디에서 정하는지도 함께 알린다. */}
            <Text
              variant={hatcheryTextModes["caption-normal"]}
              className={styles.toolbarHint}
              title="보이는 구간은 아래 재생 바의 확대 비율이 정합니다."
            >
              {window.start.toFixed(2)}초–{window.end.toFixed(2)}초
            </Text>
          </div>
        ) : null}
        <section
          aria-label={gripperOnly ? "그리퍼 상세" : "운동 상세"}
          aria-busy={detailNotice?.tone === "busy" || undefined}
        >
          {detailNotice === null ? null : (
            <div className={styles.detailNotice}>
              {detailNotice.tone === "busy" ? (
                <LoadingState size="sm">{detailNotice.text}</LoadingState>
              ) : (
                <EmptyState
                  size="sm"
                  tone={detailNotice.tone === "error" ? "error" : "neutral"}
                  action={{ label: "상세 다시 시도", onClick: refresh, icon: <RefreshIcon /> }}
                >
                  {detailNotice.text}
                </EmptyState>
              )}
            </div>
          )}
          {gripperOnly ? null : tab === "motion" ? (
            <MotionChannelGrid
              {...grid}
              cards={motionCardsWithMetrics(cards, isPrimaryMotionMetric)}
              empty="속력·가속도 크기를 낸 채널이 없습니다."
            />
          ) : (
            <MotionChannelGrid
              {...grid}
              cards={motionCardsWithMetrics(cards, isExtraMotionMetric)}
              empty="추가 지표가 없습니다."
            />
          )}
          {/* 설정은 탭을 바꿔도 유지한다. 숨긴 행은 차트를 마운트하지 않는다. */}
          <div className={styles.gripperBlock} hidden={!gripperOnly && tab !== "motion"}>
            <GripperSegmentRows
              channels={channels}
              transport={enabled ? transport : null}
              source={source}
              active={enabled && (gripperOnly || tab === "motion")}
              window={window}
              onSelect={select}
            />
          </div>
        </section>
        {!gripperOnly && overview.skipped_channels.length ? (
          <details>
            <summary>계산할 수 없는 채널 {overview.skipped_channels.length}개</summary>
            <ul>
              {overview.skipped_channels.map((s) => (
                <li key={s.channel}>
                  {channelTitle(s.channel, channels.get(s.channel))}:{" "}
                  {s.reason === "invalid_timeline"
                    ? "시간 순서 또는 시각이 유효하지 않음"
                    : s.reason === "non_numeric_values"
                      ? "숫자 값이 아님"
                      : "지원하지 않는 위치 성분"}
                </li>
              ))}
            </ul>
          </details>
        ) : null}
      </Scrollbar>
    </div>
  );
}
